import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';
import { Location } from 'history';
import URLSearchParams from '@ungap/url-search-params';
import { useEffect, useState } from 'react';
import {
    DawaAutocompleteResult,
    DawaAutocompleteResultItem,
} from '../models/dawa';
import { AddressInfo, InfrastructureOwner } from '../models/sows';
import {
    ADDRESS_INFO_CACHE_KEY,
    STOFA_BAROLO_REDIRECT_URL_COOKIE,
} from './cookies';
import sows from './sows';
import Cookies from 'js-cookie';
import dawa from './dawa';

export const PRODUCTION_URL = ''; //'//stofa-unified-flow.netlify.app/';

export function getAssetPath(): string {
    return process.env.NODE_ENV === 'production' ? PRODUCTION_URL : '';
}

/**
 * @param selector - a valid CSS selector
 * @param offset - pixel vertical offset. Use negative number to show content above target
 */
export function scrollToElement(selector: string, offset: number = 0): void {
    const $target = document.querySelector<HTMLElement>(selector);
    if ($target) {
        window.scroll({
            top: $target.offsetTop + offset,
            behavior: 'smooth',
        });
    }
}

export function getDawaIdFromLocation(
    location: Location
): string | 'null' | null {
    const query_params = new URLSearchParams(location.search);
    const dawa_id = query_params.get('dawa_id');
    if (dawa_id && dawa_id.length > 0) {
        return dawa_id;
    } else {
        return null;
    }
}

export function getStrategyOverride(): InfrastructureOwner | undefined {
    // return Cookies.get('strategy') as InfrastructureOwner;
    return 'cerillion';
}

export function useWindowSize(): { width: number; height: number } {
    function getSize() {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
        };
    }

    const [windowSize, setWindowSize] = useState(getSize);

    useEffect(() => {
        function handleResize() {
            setWindowSize(getSize());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
}

export interface StoredAddressInfo {
    address: DawaAutocompleteResultItem;
    addressInfo: AddressInfo;
}

const getAndCacheNewAddressInfo = async (
    dawaId: string
): Promise<StoredAddressInfo | null> => {
    try {
        console.log('loadAddressData: Cache miss. Refresh address info');
        const dawa_url = `/adresser/autocomplete?id=${dawaId}`;
        const dawa_result: AxiosResponse<DawaAutocompleteResult> = await dawa.get(
            dawa_url
        );
        const addressInfo: AxiosResponse<AddressInfo> = await sows.get(
            `/getAddressInfo?dawa_id=${dawaId}`
        );

        if (!!dawa_result && !!dawa_result.data && !!addressInfo) {
            const returnValue: StoredAddressInfo = {
                address: dawa_result.data[0],
                addressInfo: addressInfo.data,
            };

            Cookies.set(ADDRESS_INFO_CACHE_KEY, JSON.stringify(returnValue));

            return returnValue;
        } else {
            return null;
        }
    } catch (err) {
        Sentry.captureException(err, {
            tags: { method: 'utilities.helpers.getAndCacheNewAddressInfo' },
        });

        return null;
    }
};

/**
 * @deprecated Should use unifiedStore instead
 * @param dawaId
 */
export async function loadAddressData(
    dawaId: string
): Promise<StoredAddressInfo | null> {
    try {
        const serializedStoredAddressInfo: string | undefined = Cookies.get(
            ADDRESS_INFO_CACHE_KEY
        );

        let returnValue: StoredAddressInfo | null;

        if (!!serializedStoredAddressInfo) {
            const storedAddressInfo: StoredAddressInfo = JSON.parse(
                serializedStoredAddressInfo
            );

            if (
                storedAddressInfo &&
                storedAddressInfo.address &&
                storedAddressInfo.address.adresse &&
                storedAddressInfo.address.adresse.id === dawaId
            ) {
                returnValue = storedAddressInfo;
            } else {
                returnValue = await getAndCacheNewAddressInfo(dawaId);
            }
        } else {
            returnValue = await getAndCacheNewAddressInfo(dawaId);
        }

        // Barolo side effect
        if (
            returnValue &&
            returnValue.addressInfo &&
            returnValue.addressInfo.barolo &&
            returnValue.addressInfo.barolo.url
        ) {
            Cookies.set(
                STOFA_BAROLO_REDIRECT_URL_COOKIE,
                returnValue.addressInfo.barolo.url
            );
        }

        return returnValue;
    } catch (err) {
        Sentry.captureException(err, {
            tags: { method: 'utilities.helpers.loadAddressData' },
        });

        return null;
    }
}

export async function getDawaIDFromAddressString(
    address: string
): Promise<string | null> {
    try {
        const dawa_url = `/adresser/autocomplete?q=${address}`;
        const dawa_result: AxiosResponse<DawaAutocompleteResult> = await dawa.get(
            `${dawa_url}`
        );

        if (dawa_result && dawa_result.data && dawa_result.data.length > 0) {
            return dawa_result.data![0]!.adresse!.id;
        } else {
            return null;
        }
    } catch (err) {
        Sentry.captureException(err, {
            tags: { method: 'utilities.helpers.getDawaIDFromAddressString' },
        });
        return null;
    }
}
